
import React, { createContext, useState, useEffect, type ReactNode } from 'react';
import {
  login as loginApi,
  register as registerApi,
  logout as logoutApi,
  getMe,
  refreshToken as refreshTokenApi,
  forgotPassword as forgotPasswordApi,
  resetPassword as resetPasswordApi,
  verifyEmail as verifyEmailApi,
  setAuthData,
  clearAuthData,
  getStoredUser,
  isAuthenticated,
  type User,
  type RegisterData,
} from '../services/api';
import toast from 'react-hot-toast';
import api from '../services/api';

// ============================================
// TYPES
// ============================================

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  refreshToken: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  hasRole: (roles: Array<'customer' | 'provider' | 'admin'>) => boolean;
  isCustomer: boolean;
  isProvider: boolean;
  isAdmin: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);


const normalizeUser = (user: any): User => {
  if (!user) return user;
 
  if (user.role && typeof user.role === 'object' && user.role.name) {
    return { ...user, role: user.role.name };
  }

  return user;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  
  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated()) {
        try {
          const storedUser = getStoredUser();
          if (storedUser) {
            // Normalize stored user
            const normalized = normalizeUser(storedUser);
            setUser(normalized);
            // Verify token is still valid (optional)
            await refreshToken();
          } else {
            // Token exists but no user data – fetch from server
            const response = await getMe();
            const normalized = normalizeUser(response.data);
            setUser(normalized);
            localStorage.setItem('user', JSON.stringify(normalized));
          }
        } catch (error) {
          // Token invalid – clear storage
          clearAuthData();
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // ---------- Login ----------
  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await loginApi({ email, password });
      const { user, accessToken, refreshToken } = response.data;
      // Normalize role before storing
      const normalizedUser = normalizeUser(user);
      setAuthData({ accessToken, refreshToken, user: normalizedUser });
      setUser(normalizedUser);
      toast.success(`Welcome back, ${normalizedUser.first_name}!`);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed. Please try again.';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- Register ----------
  const register = async (data: RegisterData) => {
    setIsLoading(true);
    try {
      const response = await registerApi(data);
      const { user, accessToken, refreshToken } = response.data;
      const normalizedUser = normalizeUser(user);
      setAuthData({ accessToken, refreshToken, user: normalizedUser });
      setUser(normalizedUser);
      toast.success('Account created successfully!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Registration failed. Please try again.';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- Logout ----------
  const logout = async () => {
    try {
      await logoutApi();
    } catch (error) {
      // Silently ignore logout errors
    } finally {
      clearAuthData();
      setUser(null);
      toast.success('Logged out successfully');
    }
  };

  // ---------- Refresh Token ----------
  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token available');
      const response = await refreshTokenApi(refreshToken);
      const { accessToken, refreshToken: newRefreshToken } = response.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', newRefreshToken);
    } catch (error) {
      clearAuthData();
      setUser(null);
      throw error;
    }
  };

  // ---------- Update Profile ----------
  const updateProfile = async (data: Partial<User>) => {
    setIsLoading(true);
    try {
      const response = await api.patch('/auth/profile', data);
      const updatedUser = normalizeUser(response.data);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      toast.success('Profile updated successfully!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update profile';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- Forgot Password ----------
  const forgotPassword = async (email: string) => {
    try {
      await forgotPasswordApi(email);
      toast.success('Password reset link sent to your email');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to send reset link';
      toast.error(message);
      throw error;
    }
  };

  // ---------- Reset Password ----------
  const resetPassword = async (token: string, newPassword: string) => {
    try {
      await resetPasswordApi({ token, newPassword });
      toast.success('Password reset successfully');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to reset password';
      toast.error(message);
      throw error;
    }
  };

  // ---------- Verify Email ----------
  const verifyEmail = async (token: string) => {
    try {
      await verifyEmailApi(token);
      if (user) {
        const updatedUser = { ...user, is_verified: true };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
      }
      toast.success('Email verified successfully');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to verify email';
      toast.error(message);
      throw error;
    }
  };

  // ---------- Helpers ----------
  const hasRole = (roles: Array<'customer' | 'provider' | 'admin'>) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    register,
    updateProfile,
    refreshToken,
    forgotPassword,
    resetPassword,
    verifyEmail,
    hasRole,
    isCustomer: user?.role === 'customer',
    isProvider: user?.role === 'provider',
    isAdmin: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};